import { useRef, useState } from 'react';
import { Sun, Moon, Monitor, Palette, Check, ChevronDown } from 'lucide-react';
import { useTheme, type ThemeMode } from '@kss/theme';
import { useClickOutside } from '../../hooks/useClickOutside';
import { cn } from '../../utils/cn';

interface ModeOption {
  id: string;
  label: string;
  icon: typeof Sun;
  mode: ThemeMode;
  color?: 'darkblue';
}

const options: ModeOption[] = [
  { id: 'light', label: 'Light', icon: Sun, mode: 'light' },
  { id: 'dark', label: 'Dark', icon: Moon, mode: 'dark' },
  { id: 'auto', label: 'Auto', icon: Monitor, mode: 'auto' },
  { id: 'darkblue', label: 'Dark Blue', icon: Palette, mode: 'dark', color: 'darkblue' },
];

export default function ColorModeToggle() {
  const { mode, colorTheme, setMode, setColorTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false));

  const current = (() => {
    if (mode === 'dark' && colorTheme === 'darkblue') return options[3];
    if (mode === 'auto') return options[2];
    if (mode === 'dark') return options[1];
    return options[0];
  })();

  const CurrentIcon = current.icon;

  const choose = (opt: ModeOption) => {
    setMode(opt.mode);
    if (opt.color) {
      setColorTheme(opt.color);
    } else if (colorTheme === 'darkblue') {
      setColorTheme('default');
    }
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-8 items-center gap-1 rounded-lg px-2 text-content-muted hover:bg-surface-card-hover hover:text-content-primary transition-colors"
        title="Theme mode"
      >
        <CurrentIcon size={18} />
        <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-44 rounded-lg border border-border bg-surface-card py-1 shadow-lg">
          {options.map((opt) => {
            const active = current.id === opt.id;
            const Icon = opt.icon;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => choose(opt)}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors',
                  active
                    ? 'bg-primary-light text-primary font-medium'
                    : 'text-content-secondary hover:bg-surface-card-hover'
                )}
              >
                <Icon size={14} />
                <span className="flex-1 text-left">{opt.label}</span>
                {active && <Check size={14} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
